<template>
    <div
        :class="`formulate-input-element formulate-input-element--${context.class}`"
        :data-type="context.type"
    >

        <FileSlingerListUploader
            :category="fileCategory"
            :parentId="parentId"
            :existingFiles="initialFileObjects"
            inputHeight="120px"
            inputWrapperClass="w-100"
            listWrapperClass="w-100"
            wrapperClass=""
            :hideLimits="hideLimits"
            :imageOnly="imageOnly"
            :accept="accept"
            :maxItems="localMaxItems"
            :maxFilesize="maxFilesize"
            :maxTotalFilesize="maxTotalFilesize"
            :disabled="readOnly"
            :disabledPlaceholderText="`No Files`"
            v-bind="context.attributes"
            @update="handleListUpdate"
        ></FileSlingerListUploader>

        <!-- v-bind="$attrs" - $attrs has other things that you told formulate are component props that this does not have declared as props
        such as 'meta' for the new formulate mixin  
        but context.attributes has other things that you set as props on formulate-input that this does not have declared as props
        which is important here... so we can pass props from formulate-input to file-slinger-list-uploader without declaring them all here 
        NOTE - when you do that with booleans, you need to use :the-prop="true" instead of just the-prop
        -->

    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import FileSlingerListUploader from '../s3_files/FileSlingerListUploader.vue';

// formulate custom inputs:  must read from and write to context.model
// for this, we work with full objects representing the files?  or just IDs???
// and always work with arrays?  in and out?
// you COULD just work with IDs, then fetch here or in list component if needed... since you have category, etc.
// working with IDs would be safer... so your form data change detection works... otherwise you could have infinite loops if you post/get slightly different objects, they will look different..
// but if only IDs, then backend pydantic file model must be able to work with only IDs incoming data (like a choice model or django model wrapper)

// HANDLES SINGLE FILES USING 'single' PROP - as of March 28 2023

export default {
    name: 'FormulateS3FileUploader',
    components: {
        FileSlingerListUploader,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        context: {  // the default/required prop for Vue-Formulate field components
            type: Object,
            required: true,
        },
        fileCategory: {
            type: String,
            required: true,
        },
        parentId: {
            type: String,
            required: false,  // making this false, since the component we wrap allows false... it really depends on the file category, some need it some don't
        },
        single: {  // if you ONLY WANT A SINGLE FILE (value is single object, not array), set this true -- otherwise value is array
            // why not use 'multiple' instead?  because that is an existing prop for vue formulate, so it gets confusing, and most of the time you'll have multiple, so that's our default
            type: Boolean,
            default: false,
        },
        maxItems: {  // disables input if we are at max number - default is 10 just to be safe... but you can increase, or set null to allow unlimited
            type: Number,
            default: 10,  // just to be safe... can change if needed
        },
        imageOnly: {  // shortcut for only accepting images - NOT USED IF YOU HAVE SOMETHING IN 'accept' prop
            type: Boolean,
            default: false,
        },
        accept: {  // Array or string of comma-separated mime-types or .extensions - HTML standard - if array we join to string
            // SUPERCEDES imageOnly
            type: String,
            default: '',
        },
        maxFilesize: {  // max for ONE file in bytes - make sure it comes from / matches your backend 
            type: Number,
            default: (1024 * 1024 * 10),  // 10MB default - must make sure your s3file class can take it!
        },
        maxTotalFilesize: {  // TOTAL of all files - useful for things like email attachments when we have limit of all files combined
            // maybe allow strings here... parse them to size... must be npm package for it...
            type: Number,
            required: false,
        },
        hideLimits: {  // hide things like max files, max filesize, etc. shown under the drop zone
            type: Boolean,
            default: false,
        },
        // and we pass on the rest of $attrs to the main component 
    },
    data() {
        return {
            // always keep arrays here, even if this.single === true
            initialFileObjects: [],  // if we are not doing v-model... only setting this with initial value, then reporting back up?
            localFileIds: [],  
            localFileObjects: [],
            localMaxItems: this.maxItems,  // override to 1 if single=true in created() 
        }
    },
    computed: {
        readOnly() {
            // this is for later... for showing in complete read-only mode
            return this.context.attributes?.disabled ?? false;
        },
    },
    created() {
        // set the local things... but this will not be reactive unless you set a watcher...
        // or just pass context.model to existing

        // we will filter out any falsey values in the array before setting on internal component
        // why? a temporary workaround for backend having stale data, still having reference to a file that was deleted, not finding it, and using None as its value in the list
        // root cause of that is front-end form submission, does not submit empty list when last file was deleted if field is required... so need to fix that first, then you could possibly remove this
        // but probably no harm to keep the falsey filter here... just might cover up other bugs, which is not ideal

        // must deal with 'single' prop - internal data is ALWAYS array though
        // we will just ignore single prop here and always convert to array internally... will work whether we get array or single object 
        // but we must override any maxItems to 1 if single = true

        if (this.single) {
            this.localMaxItems = 1;
        }

        const hasIncomingValue = !!this.context.model;

        if (!hasIncomingValue) {
            this.initialFileObjects = [];  // no incoming?  just set internal initial value to empty array
        } else {
            // we have some value..
            const incomingFileArrayRaw = Array.isArray(this.context.model) ? [...this.context.model] : [this.context.model];
            // now filter that (in case you had nulls in the array... which can happen if something is goofy in backend)
            // then make a copy of all objects so we don't mutate the stuff we passed in
            this.initialFileObjects = incomingFileArrayRaw.filter((f) => !!f).map(fileObj => ({...fileObj}));
        }
        // const incomingFiles = (!!this.context.model && Array.isArray(this.context.model)) ? this.context.model.filter((f) => !!f) : [];
        // console.log('HELLO from FormulateS3FileUploader - incoming files:', incomingFiles);
        // this.initialFileObjects = incomingFiles.map(file => ({...file}));  // [...incomingFiles];
    },
    mounted() {
    },
    methods: {
        handleListUpdate(newList) {
            // newList will be array of objects with our file data (standard format to/from backend for s3 files)
            console.log('new file list', newList);
            this.localFileObjects = newList.map(f => ({...f}));  // shallow copy, always an array
            this.localFileIds = newList.map(f => f.id);
            // now set value in form
            // must change this based on whether we have this.single === true
            if (this.single) {
                this.context.model = this.localFileObjects.length ? this.localFileObjects[0] : null;
            } else {
                this.context.model = this.localFileObjects;   // make a shallow copy??? 
            }
            // also, call blurHandler from formulate input context to trigger updates when we listen for that in forms
            this.$nextTick(() => {
                // need to wrap in nextTick to allow for model to be updated I believe...
                // hmmm... this is still happening before the values are actually updated... maybe need to d a nextTick on form level?
                this.context.blurHandler();
            });
        }
    }
}
</script>

<style>

</style>